import { Pencil } from "lucide-react";

import type { Project } from "@/api/development_suite";
import { formatDateTime } from "@/components/development_suite/developmentSuiteQueries";
import { ProjectContactsSection } from "@/components/development_suite/ProjectContactsSection";
import { ProjectRecordsSection } from "@/components/development_suite/ProjectRecordsSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

function DetailField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={value ? "break-words text-sm" : "text-sm text-muted-foreground"}>{value || "Not set"}</dd>
    </div>
  );
}

/**
 * The opened project: its records are the primary content, with the project's
 * own details beside them and secondary sections collapsed below.
 */
export function ProjectDetail({
  municipalityId,
  project,
  onEdit,
}: {
  municipalityId: number;
  project: Project;
  onEdit: () => void;
}) {
  return (
    <div className="mx-auto grid w-full max-w-6xl gap-6 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-8">
      <div className="min-w-0 space-y-8">
        <ProjectRecordsSection municipalityId={municipalityId} projectId={project.id} kind="permits" />
        <ProjectRecordsSection municipalityId={municipalityId} projectId={project.id} kind="planning_records" />
        <ProjectContactsSection municipalityId={municipalityId} projectId={project.id} />
      </div>

      <aside aria-label="Project details" className="min-w-0 space-y-2">
        <section aria-labelledby="project-details-heading" className="space-y-4 pb-4">
          <div className="flex items-center justify-between gap-2">
            <h2 id="project-details-heading" className="text-sm font-semibold">
              Project details
            </h2>
            <Button size="sm" variant="outline" onClick={onEdit}>
              <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
              Edit project
            </Button>
          </div>
          <dl className="space-y-3">
            <DetailField label="Site address" value={project.site_address} />
            <DetailField label="Parcel ID" value={project.parcel_identifier} />
            <DetailField label="Responsible staff" value={project.responsible_user?.email} />
          </dl>
        </section>

        <Accordion type="multiple">
          <AccordionItem value="description" className="border-b-0 border-t">
            <AccordionTrigger className="text-left">Description</AccordionTrigger>
            <AccordionContent>
              <p
                className={
                  project.description ? "whitespace-pre-wrap break-words" : "text-muted-foreground"
                }
              >
                {project.description || "No description yet."}
              </p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="history" className="border-b-0 border-t">
            <AccordionTrigger className="text-left">Project history</AccordionTrigger>
            <AccordionContent>
              <dl className="space-y-3">
                <DetailField
                  label="Created"
                  value={`${formatDateTime(project.created_at)}${project.created_by ? ` by ${project.created_by.email}` : ""}`}
                />
                <DetailField label="Last saved" value={formatDateTime(project.updated_at)} />
              </dl>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </aside>
    </div>
  );
}
